<template>
  <!--
    "Stay in the loop". Solo código (no Prismic).
    Alcance: armado estático + validación de email + estados (idle/sending/success/error)
    + captura automática del CLC (fallback "Other" cuando no hay ?center=). El envío real
    va a una Cloudpage de SFMC (Salesforce Marketing Cloud).
  -->
  <section class="stay-loop">
    <div class="stay-loop__inner">
      <div class="stay-loop__left">
        <h2 class="stay-loop__title">{{ t.title }}</h2>
        <p class="stay-loop__desc">{{ t.desc }}</p>
      </div>

      <form class="stay-loop__form" novalidate @submit.prevent="onSubmit">
        <!-- Email (único campo editable) -->
        <div class="stay-loop__field">
          <label class="stay-loop__label" for="stay-loop-email">{{ t.emailLabel }}</label>
          <div class="stay-loop__pill" :class="{ 'stay-loop__pill--error': state === 'error' }">
            <input
              id="stay-loop-email"
              v-model.trim="email"
              type="email"
              class="stay-loop__input"
              :placeholder="t.emailPlaceholder"
              :disabled="isBusy"
              autocomplete="email"
              @input="onInput"
            />
          </div>
        </div>

        <!-- Your center: solo lectura (se toma del ?center= / sessionStorage; fallback Default) -->
        <div class="stay-loop__field">
          <span class="stay-loop__label">{{ t.centerLabel }}</span>
          <div class="stay-loop__pill stay-loop__pill--readonly">
            <svg class="stay-loop__lock" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#26272b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span class="stay-loop__center">{{ centerName }}</span>
          </div>
        </div>

        <button type="submit" class="stay-loop__submit" :disabled="isBusy">
          {{ submitLabel }}
        </button>

        <p v-if="state === 'error'" class="stay-loop__msg stay-loop__msg--error" role="alert">{{ errorMsg }}</p>
        <p v-else-if="state === 'success'" class="stay-loop__msg stay-loop__msg--success" role="status">{{ t.success }}</p>
      </form>

      <p class="stay-loop__small">{{ t.small }}</p>
    </div>
  </section>
</template>

<script>
import { executeRecaptcha, loadRecaptcha, hasRecaptchaKey } from '../helpers/recaptchaHelpers';

// Nombre de acción reCAPTCHA v3 (verificable del lado de la Cloudpage).
const RECAPTCHA_ACTION = 'newsletter_subscribe';

// URL de la Cloudpage de SFMC que recibe el POST del form. Configurable por entorno.
const CLOUDPAGE_URL = process.env.VUE_APP_NEWSLETTER_CLOUDPAGE_URL || '';

export default {
  name: 'StayInTheLoop',
  props: {
    prismicLang: {
      type: String,
      required: true,
      validator: (value) => ['en-us', 'es-es'].includes(value)
    },
    // Nombre del CLC actual (se muestra en el campo read-only).
    clcName: {
      type: String,
      default: ''
    },
    // Segmento del CLC (?center=). Viaja en el payload; fallback 'other'.
    clcSegmentValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      email: '',
      state: 'idle', // idle | sending | success | error
      errorMsg: ''
    };
  },
  computed: {
    t() {
      const texts = {
        'en-us': {
          title: 'Stay in the loop',
          desc: 'Get all our latest updates and news straight to your inbox.',
          small: 'We only send emails worth reading.',
          emailLabel: 'Email address',
          emailPlaceholder: 'you@example.com',
          centerLabel: 'Your center',
          subscribe: 'Subscribe',
          sending: 'Subscribing…',
          success: 'Almost done!\nPlease check your email to verify your address.',
          invalidEmail: 'Please enter a valid email address.',
          recaptchaError: "We couldn't verify you're human. Please try again.",
          submitError: 'Something went wrong. Please try again.'
        },
        'es-es': {
          title: 'Mantente al día',
          desc: 'Recibe todas nuestras novedades y noticias directamente en tu bandeja de entrada.',
          small: 'Solo te enviaremos correos que valga la pena leer.',
          emailLabel: 'Correo electrónico',
          emailPlaceholder: 'tu@ejemplo.com',
          centerLabel: 'Tu centro',
          subscribe: 'Suscribirme',
          sending: 'Suscribiendo…',
          success: '¡Casi listo!\nPor favor, revisa tu correo electrónico para verificar tu dirección.',
          invalidEmail: 'Ingresa un correo electrónico válido.',
          recaptchaError: 'No pudimos verificar que eres una persona. Prueba de nuevo.',
          submitError: 'Algo salió mal. Prueba de nuevo.'
        }
      };
      return texts[this.prismicLang] || texts['en-us'];
    },
    // Nombre visible del centro. Sin CLC navegado -> "Other" (no exponemos la lista de centros,
    // que incluye CLCs sin abrir). Decisión del ticket ACHIEVERY-1363.
    centerName() {
      if (this.clcName) return this.clcName;
      return this.prismicLang === 'en-us' ? 'Other' : 'Otro';
    },
    isBusy() {
      return this.state === 'sending' || this.state === 'success';
    },
    submitLabel() {
      return this.state === 'sending' ? this.t.sending : this.t.subscribe;
    }
  },
  mounted() {
    // Precarga el script de reCAPTCHA al montar el form (solo en la home, donde vive este módulo)
    // así el token ya está listo al enviar y no se demora el submit. Si falla, no rompe: el
    // token se reintenta en onSubmit.
    if (hasRecaptchaKey()) {
      loadRecaptcha(this.prismicLang).catch(() => { /* se reintenta en el submit */ });
    }
  },
  methods: {
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    onInput() {
      if (this.state === 'error') {
        this.state = 'idle';
        this.errorMsg = '';
      }
    },
    async onSubmit() {
      if (this.isBusy) return;

      if (!this.isValidEmail(this.email)) {
        this.state = 'error';
        this.errorMsg = this.t.invalidEmail;
        return;
      }

      this.state = 'sending';

      // reCAPTCHA v3: token de un solo uso para esta acción. Sin token no seguimos, porque la
      // Cloudpage de SFMC lo va a exigir/verificar (siteverify con la clave secreta).
      let recaptchaToken = '';
      try {
        recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);
      } catch (e) {
        this.state = 'error';
        this.errorMsg = this.t.recaptchaError;
        return;
      }

      const payload = {
        email: this.email,
        clc: this.clcSegmentValue || 'other',
        clcName: this.centerName,
        lang: this.prismicLang === 'es-es' ? 'es' : 'en',
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTION
      };

      this.handleSubmit(payload);
    },
    // Envío real a la Cloudpage de SFMC. POST con body JSON.
    // SILENT SUCCESS (decisión del ticket ACHIEVERY-1363): NUNCA se le reporta al usuario un fallo
    // de la suscripción — ni "ya estabas suscripto" ni errores del backend. Se intenta el POST
    // best-effort y SIEMPRE se muestra éxito (la API además responde OK en esos casos). Por eso no
    // se lee la respuesta. El server verifica el reCAPTCHA (siteverify) y segmenta por `clc`.
    async handleSubmit(payload) {
      // Analytics / GTM: evento para que analytics lo capture.
      // NO se envía el email en el push (dato personal); solo el segmento de CLC.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'stay_in_the_loop',
        clc: payload.clc
      });

      // Éxito optimista inmediato (reset del correo incluido); el resultado real no se expone.
      this.state = 'success';
      this.email = '';

      if (CLOUDPAGE_URL) {
        try {
          await fetch(CLOUDPAGE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } catch (e) {
          // Silencioso: no se muestra error al usuario.
        }
      }
    }
  }
};
</script>
